import { useState, useEffect, useRef } from 'react'
import { getSessionSafe } from '../../lib/supabase'

export default function LocationAddressInput({ label, value, onChange }) {
  const [input, setInput] = useState(value)
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceTimer = useRef(null)

  useEffect(() => {
    setInput(value)
  }, [value])

  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([])
      return
    }

    setLoading(true)
    try {
      const session = await getSessionSafe()
      if (!session || !session.access_token) {
        console.error('No valid session for geocoding')
        setSuggestions([])
        setShowSuggestions(false)
        setLoading(false)
        return
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tomtom-geocode?q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      )

      if (!res.ok) {
        console.error('Geocoding error:', res.status)
        return
      }

      const data = await res.json()
      setSuggestions(Array.isArray(data) ? data : [])
      setShowSuggestions(true)
    } catch (err) {
      console.error('Geocoding fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const query = e.target.value
    setInput(query)

    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(query)
    }, 300)
  }

  const handleSelectSuggestion = (suggestion) => {
    setInput(suggestion.address)
    onChange(suggestion.address, suggestion.lat, suggestion.lon)
    setSuggestions([])
    setShowSuggestions(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4, fontWeight: 500 }}>
        {label}
      </label>
      <input
        type="text"
        value={input}
        onChange={handleInputChange}
        onFocus={() => input && suggestions.length > 0 && setShowSuggestions(true)}
        placeholder="Digita indirizzo..."
        style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: 4,
          border: '1px solid var(--border)',
          fontSize: '0.9rem',
          backgroundColor: 'var(--bg-input)',
          color: 'var(--text)',
        }}
      />
      {loading && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
          Ricerca in corso...
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {suggestions.map((s, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelectSuggestion(s)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 10px',
                textAlign: 'left',
                fontSize: '0.9rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: idx < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                color: 'var(--text)',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = 'var(--bg-hover)')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
            >
              {s.address}
            </button>
          ))}
        </div>
      )}
      {showSuggestions && !loading && suggestions.length === 0 && input && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            padding: '8px 10px',
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontSize: '0.85rem',
            color: 'var(--text-muted)',
          }}
        >
          Nessun risultato trovato
        </div>
      )}
    </div>
  )
}
